import { Box, Typography, Grid } from '@mui/material';
import { useGetList } from 'react-admin';
import { useState } from 'react';
import { DRecord } from './DashboardCard';
import { CriticalAlerts, WorkflowStatus, ExpiringCertifications, ContractAlertsList, ComplianceCalendar } from './DashboardActivity';
import { HeadcountByDepartment, DemographicsPanel, OnboardingByStageChart, TrainingStatusChart, DisciplinaryByStageChart, InvoiceStatusChart, DSARStatusChart } from './DashboardChart';
import { StatsRow, FinanceOverview, SkillsDevelopment, PIPsSection, TerminationsSection } from './DashboardMetrics';

const getCostAmount = (cost: unknown): number => {
  if (!cost) return 0;
  if (typeof cost === 'number') return cost;
  if (typeof cost === 'object' && (cost as Record<string, unknown>).amount != null) return Number((cost as Record<string, unknown>).amount);
  return 0;
};

export const Dashboard = () => {
  const [now] = useState(() => Date.now());
  const { data: employees } = useGetList('employees', { pagination: { page: 1, perPage: 1000 } });
  const { data: trainingRecords } = useGetList('training-records', { pagination: { page: 1, perPage: 1000 } });
  const { data: ohsCertifications } = useGetList('ohs-certifications', { pagination: { page: 1, perPage: 1000 } });
  const { data: terminations } = useGetList('terminations', { pagination: { page: 1, perPage: 1000 } });
  const { data: disciplinaryCases } = useGetList('disciplinary-cases', { pagination: { page: 1, perPage: 1000 } });
  const { data: workflowInstances } = useGetList('workflow-instances', { pagination: { page: 1, perPage: 500 } });
  const { data: invoices } = useGetList('invoices', { pagination: { page: 1, perPage: 1000 } });
  const { data: receipts } = useGetList('receipts', { pagination: { page: 1, perPage: 1000 } });
  const { data: contractAlerts } = useGetList('contract-alerts', { pagination: { page: 1, perPage: 1000 } });
  const { data: complianceCalendar } = useGetList('compliance-calendar', { pagination: { page: 1, perPage: 1000 } });
  const { data: dsarRequests } = useGetList('dsar-requests', { pagination: { page: 1, perPage: 1000 } });
  const { data: pips } = useGetList('pips', { pagination: { page: 1, perPage: 1000 } });

  const activeCount = employees?.filter((e: DRecord) => e.status === 'Active').length ?? 0;
  const onboardingCount = employees?.filter((e: DRecord) => e.currentStage && e.currentStage !== 'Complete').length ?? 0;
  const pendingActions = (disciplinaryCases?.filter((d: DRecord) => d.currentStage !== 'Closed').length ?? 0) + (terminations?.filter((t: DRecord) => t.status === 'Pending' || t.status === 'PendingNotice' || t.status === 'PendingClearance').length ?? 0);
  const expiringCerts = ohsCertifications?.filter((c: DRecord) => {
    const expiry = new Date(c.expiryDate as string);
    const days = Math.ceil((expiry.getTime() - now) / (1000 * 60 * 60 * 24));
    return days <= 30 && days > 0;
  }).length ?? 0;

  const totalInvoiced = invoices?.reduce((sum: number, i: DRecord) => sum + (i.total as number || i.amount as number || 0), 0) ?? 0;
  const totalReceived = receipts?.reduce((sum: number, r: DRecord) => sum + (r.amount as number || r.total as number || 0), 0) ?? 0;
  const overdueInvoices = invoices?.filter((i: DRecord) => i.status === 'Overdue').length ?? 0;
  const invoiceStatusData = invoices ? [
    { label: 'Paid', value: invoices.filter((i: DRecord) => i.status === 'Paid').length },
    { label: 'Issued', value: invoices.filter((i: DRecord) => i.status === 'Issued').length },
    { label: 'Overdue', value: invoices.filter((i: DRecord) => i.status === 'Overdue').length },
  ] : [];

  const pendingAlerts = contractAlerts?.filter((a: DRecord) => a.status === 'Pending' || a.status === 'Triggered').length ?? 0;
  const probationAlerts = contractAlerts?.filter((a: DRecord) => a.alertType === 'ProbationExpiry' && (a.status === 'Pending' || a.status === 'Triggered')).length ?? 0;
  const fixedTermAlerts = contractAlerts?.filter((a: DRecord) => a.alertType === 'FixedTermExpiry' && (a.status === 'Pending' || a.status === 'Triggered')).length ?? 0;
  const expiringContracts = contractAlerts?.filter((a: DRecord) => {
    const trigger = new Date(a.triggerDate as string);
    const days = Math.ceil((trigger.getTime() - now) / (1000 * 60 * 60 * 24));
    return days <= 30 && (a.status === 'Pending' || a.status === 'Triggered');
  }).length ?? 0;

  const upcomingDeadlines = complianceCalendar?.filter((c: DRecord) => {
    const due = new Date(c.dueDate as string);
    const days = Math.ceil((due.getTime() - now) / (1000 * 60 * 60 * 24));
    return days > 0 && days <= 90;
  }).slice(0, 5) ?? [];
  const overdueCompliance = complianceCalendar?.filter((c: DRecord) => {
    const due = new Date(c.dueDate as string);
    return due.getTime() < now;
  }).length ?? 0;

  const pendingDSAR = dsarRequests?.filter((d: DRecord) => d.status === 'Pending' || d.status === 'InProgress').length ?? 0;
  const dsarStatusData = dsarRequests ? [
    { label: 'Pending', value: dsarRequests.filter((d: DRecord) => d.status === 'Pending').length },
    { label: 'In Progress', value: dsarRequests.filter((d: DRecord) => d.status === 'InProgress').length },
    { label: 'Completed', value: dsarRequests.filter((d: DRecord) => d.status === 'Completed').length },
  ] : [];

  const trainingCost = trainingRecords?.reduce((sum: number, t: DRecord) => sum + getCostAmount(t.cost), 0) ?? 0;
  const trainingBudget = 500000;
  const trainingUtilization = Math.round((trainingCost / trainingBudget) * 100);
  const trainingStatusData = trainingRecords ? [
    { label: 'Completed', value: trainingRecords.filter((t: DRecord) => t.status === 'Completed').length },
    { label: 'In Progress', value: trainingRecords.filter((t: DRecord) => t.status === 'InProgress').length },
    { label: 'Planned', value: trainingRecords.filter((t: DRecord) => t.status === 'Planned').length },
  ] : [];

  const resolveEmpName = (ref: { id: string } | null | undefined): string => {
    if (!ref) return 'Unknown';
    const emp = employees?.find((e: DRecord) => e._id === ref.id);
    return emp ? `${emp.legalEntity?.firstName ?? ''} ${emp.legalEntity?.lastName ?? ''}`.trim() || 'Unknown' : 'Unknown';
  };

  const raceData = employees ? [
    { label: 'African', value: employees.filter((e: DRecord) => e.demographics?.race === 'African').length },
    { label: 'Coloured', value: employees.filter((e: DRecord) => e.demographics?.race === 'Coloured').length },
    { label: 'Indian', value: employees.filter((e: DRecord) => e.demographics?.race === 'Indian').length },
    { label: 'White', value: employees.filter((e: DRecord) => e.demographics?.race === 'White').length },
  ] : [];
  const genderData = employees ? [
    { label: 'Male', value: employees.filter((e: DRecord) => e.demographics?.gender === 'Male').length },
    { label: 'Female', value: employees.filter((e: DRecord) => e.demographics?.gender === 'Female').length },
  ] : [];
  const departmentData = employees ? ['Finance', 'IT', 'HR', 'Operations', 'Sales', 'Marketing', 'Legal', 'Compliance'].map(dept => ({
    label: dept,
    value: employees.filter((e: DRecord) => e.organisationalHierarchy?.department === dept).length,
  })) : [];

  const expiringSoon = ohsCertifications?.filter((c: DRecord) => {
    const expiry = new Date(c.expiryDate as string);
    const days = Math.ceil((expiry.getTime() - now) / (1000 * 60 * 60 * 24));
    return days <= 60 && days > 0;
  }).slice(0, 5) ?? [];

  const onboardingByStage = ['Documentation', 'ContractSigning', 'Training', 'Verification'].map(stage => ({
    label: stage,
    value: employees?.filter((e: DRecord) => e.currentStage === stage).length ?? 0,
  }));
  const disciplinaryByStage = ['Investigation', 'Hearing', 'Appeal', 'Closed'].map(stage => ({
    label: stage,
    value: disciplinaryCases?.filter((d: DRecord) => d.currentStage === stage).length ?? 0,
  }));

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>HR Dashboard</Typography>

      <CriticalAlerts expiringCerts={expiringCerts} overdueCompliance={overdueCompliance} pendingDSAR={pendingDSAR} expiringContracts={expiringContracts} />

      <StatsRow activeCount={activeCount} onboardingCount={onboardingCount} pendingActions={pendingActions} disciplinaryCount={disciplinaryCases?.filter((d: DRecord) => d.currentStage !== 'Closed').length ?? 0} terminationsPendingCount={terminations?.filter((t: DRecord) => t.status?.includes('Pending')).length ?? 0} expiringCerts={expiringCerts} trainingCost={trainingCost} trainingUtilization={trainingUtilization} />

      <WorkflowStatus workflowInstances={workflowInstances ?? []} />

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 8 }}><HeadcountByDepartment data={departmentData} /></Grid>
        <Grid size={{ xs: 12, md: 4 }}><DemographicsPanel raceData={raceData} genderData={genderData} /></Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}><OnboardingByStageChart data={onboardingByStage} /></Grid>
        <Grid size={{ xs: 12, md: 4 }}><TrainingStatusChart data={trainingStatusData} /></Grid>
        <Grid size={{ xs: 12, md: 4 }}><DisciplinaryByStageChart data={disciplinaryByStage} /></Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}><ExpiringCertifications certs={expiringSoon} now={now} resolveEmpName={resolveEmpName} /></Grid>
        <Grid size={{ xs: 12, md: 4 }}><ContractAlertsList alerts={contractAlerts ?? []} pendingAlerts={pendingAlerts} probationAlerts={probationAlerts} fixedTermAlerts={fixedTermAlerts} now={now} resolveEmpName={resolveEmpName} /></Grid>
        <Grid size={{ xs: 12, md: 4 }}><ComplianceCalendar deadlines={upcomingDeadlines} overdueCompliance={overdueCompliance} now={now} /></Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}><FinanceOverview totalInvoiced={totalInvoiced} totalReceived={totalReceived} invoiceCount={invoices?.length ?? 0} receiptCount={receipts?.length ?? 0} overdueInvoices={overdueInvoices} /></Grid>
        <Grid size={{ xs: 12, md: 3 }}><InvoiceStatusChart data={invoiceStatusData} /></Grid>
        <Grid size={{ xs: 12, md: 3 }}><DSARStatusChart data={dsarStatusData} /></Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 6 }}><SkillsDevelopment trainingRecords={trainingRecords ?? []} trainingBudget={trainingBudget} trainingCost={trainingCost} trainingUtilization={trainingUtilization} /></Grid>
        <Grid size={{ xs: 12, md: 3 }}><PIPsSection pips={pips ?? []} /></Grid>
        <Grid size={{ xs: 12, md: 3 }}><TerminationsSection terminations={terminations ?? []} /></Grid>
      </Grid>
    </Box>
  );
};
